import { Component, OnInit, Inject, ChangeDetectionStrategy, Optional, Output, EventEmitter, Input, HostBinding, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { sanitizeInputForCrossSiteScriptCharactersDom, sanitizeInputForCrossSiteScriptCharactersDescription, stripHtmlToText, nonWhitespaceHtmlValidator, getStatusPageErrorMessage } from '../../../../../_theme/core/_base/curd/utils/validators-util';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayoutUtilsService, MessageType } from '../../../../../_theme/core/_base/curd';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { environment } from 'src/environments/environment';
import { QuillModule } from 'ngx-quill';
import { CustomSelectComponent } from '../../components/custom-select/custom-select.component';
import { StatusDrawerQuillKeyboardDirective } from '../../directives/status-drawer-quill-keyboard.directive';
import { 
    DEFAULT_CREATE_INCIDENT_TEMPLATE_PAYLOAD, 
    DEFAULT_EDIT_INCIDENT_TEMPLATE_PAYLOAD, 
    DEFAULT_MAINTENANCE_TEMPLATE_PAYLOAD,
    CreateIncidentTemplatePayload,
    EditIncidentTemplatePayload,
    MaintenanceTemplatePayload
} from '../../models/status-page-api.models';
import { normalizeStatusPageRichText } from '../../models/status-page-rich-text-normalizer.utils';

export type TemplateType = 'incident' | 'maintenance';

export interface StatusTemplateViewModel {
    id: number;
    name: string;
    subject: string;
    body: string;
    type: TemplateType;
    updatedAt: Date;
    original?: any;
}

interface TemplateFormSnapshot {
    templateType: TemplateType;
    name: string;
    subject: string;
    body: string;
}

export function mapApiTemplateToViewModel(payload: any, type: TemplateType): StatusTemplateViewModel {
    let body = payload.descriptionTemplate;
    if (type === 'incident' && Array.isArray(payload.updateTemplates) && payload.updateTemplates.length > 0) {
        body = payload.updateTemplates[0]?.message || payload.descriptionTemplate;
    }

    return {
        id: payload.templateId,
        name: payload.name,
        subject: payload.titleTemplate,
        body,
        type,
        updatedAt: new Date(),
        original: payload
    };
}

@Component({
    selector: 'app-template-dialog',
    templateUrl: './template-dialog.component.html',
    styleUrls: ['./template-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    QuillModule,
    CustomSelectComponent,
    StatusDrawerQuillKeyboardDirective
]
})
export class TemplateDialogComponent implements OnInit {

    readonly messageEditorModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            ['link']
        ]
    };

    @Output() closed = new EventEmitter<any>();
    @Output() toggleExpand = new EventEmitter<void>();
    @Input() layout: 'dialog' | 'drawer' = 'dialog';
    @Input() selectedTemplateType: 'incident' | 'maintenance' = 'incident';
    @Input() isExpanded: boolean = false;
    @Input() showExpandToggle: boolean = true;
    @HostBinding('class.drawer-mode') get isDrawerMode() {
        return this.layout === 'drawer';
    }
    get isReadOnly(): boolean {
        return this.mode === 'view';
    }

    form: UntypedFormGroup;
    mode: 'create' | 'edit' | 'view' = 'create';
    templateTypes = [
        { label: 'Incident Template', value: 'incident', icon: 'warning_amber' },
        { label: 'Maintenance Template', value: 'maintenance', icon: 'build' }
    ];
    isSubmitting = false;
    viewLoading = false;
    bodyLength: number | null = null;

    private _dataInput: any;
    private initialFormSnapshot: TemplateFormSnapshot | null = null;
    @Input() set dataInput(val: any) {
        this._dataInput = val;
        if (val?.mode) {
            this.mode = val.mode;
        }
    }
    get dataInput() {
        return this._dataInput;
    }

    constructor(
        @Optional() public dialogRef: MatDialogRef<TemplateDialogComponent> | null,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly statusPageService: StatusPageService,
        private readonly authService: AuthService,
        private readonly cd: ChangeDetectorRef,
        private readonly layoutUtilsService: LayoutUtilsService,
        private readonly sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.viewLoading = true;
        const effectiveData = this.dataInput ?? this.data;
        this.mode = effectiveData?.mode || 'create';

        const template = effectiveData?.template;
        const initialBody = template?.body || '';
         let templateType = template?.type;
        if (this.mode == 'create') {
            templateType = this.selectedTemplateType;
        }
        this.form = new UntypedFormGroup({
            templateType: new UntypedFormControl(templateType || 'incident', Validators.required),
            name: new UntypedFormControl(template?.name || '', [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDom(this.sanitizer)]),
            subject: new UntypedFormControl(template?.subject || '', [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDom(this.sanitizer)]),
            body: new UntypedFormControl(initialBody, [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription, this.maxTextLengthValidator(5000)])
        });
        this.initialFormSnapshot = this.getFormSnapshot();
        this.bodyLength = this.getPlainTextLength(initialBody) || null;
        this.trackBodyLength();
        if (this.isReadOnly) {
            this.form.disable({ emitEvent: false });
        }
        Promise.resolve().then(() => {
            this.viewLoading = false;
            this.cd.detectChanges();
        });
    }

    get isIncidentTemplate(): boolean {
        return this.form.get('templateType')?.value === 'incident';
    }

    get isSaveDisabled(): boolean {
        if (!this.form) {
            return true;
        }

        return this.form.invalid
            || this.isSubmitting
            || this.viewLoading
            || (this.mode === 'edit' && !this.hasFormChanges());
    }

    private trackBodyLength(): void {
        const control = this.form.get('body');
        control?.valueChanges.subscribe((value) => {
            const length = this.getPlainTextLength(value);
            this.bodyLength = length === 0 ? null : length;
        });
    }

    private getPlainTextLength(value: unknown): number {
        return stripHtmlToText(value).length;
    }

    private maxTextLengthValidator(maxLength: number) {
        return (control: AbstractControl) => {
            const length = this.getPlainTextLength(control?.value);
            return length > maxLength ? { maxTextLength: { requiredLength: maxLength, actualLength: length } } : null;
        };
    }

    save() {
        if (this.isReadOnly) {
            this.close();
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        if (this.mode === 'edit' && !this.hasFormChanges()) {
            return;
        }

        if (this.mode === 'create') {
            this.createTemplate();
        } else {
            this.updateTemplate();
        }
    }

    private createTemplate() {
        const type = this.form.value.templateType as TemplateType;
        if (type === 'incident') {
            this.executeCreateIncident();
        } else {
            this.executeCreateMaintenance();
        }
    }

    private updateTemplate() {
        const type = this.form.value.templateType as TemplateType;
        if (type === 'incident') {
            this.executeUpdateIncident();
        } else {
            this.executeUpdateMaintenance();
        }
    }

    private hasFormChanges(): boolean {
        if (!this.initialFormSnapshot) {
            return true;
        }

        const currentSnapshot = this.getFormSnapshot();
        return currentSnapshot.templateType !== this.initialFormSnapshot.templateType
            || currentSnapshot.name !== this.initialFormSnapshot.name
            || currentSnapshot.subject !== this.initialFormSnapshot.subject
            || currentSnapshot.body !== this.initialFormSnapshot.body;
    }

    private getFormSnapshot(): TemplateFormSnapshot {
        const rawValue = this.form.getRawValue();
        return {
            templateType: rawValue.templateType as TemplateType,
            name: this.normalizeTextForComparison(rawValue.name),
            subject: this.normalizeTextForComparison(rawValue.subject),
            body: this.normalizeBodyForComparison(rawValue.body)
        };
    }

    private normalizeTextForComparison(value: unknown): string {
        return String(value ?? '').trim();
    }

    private normalizeBodyForComparison(value: unknown): string {
        const normalizedHtml = normalizeStatusPageRichText(value);
        if (!normalizedHtml) {
            return '';
        }

        const documentRef = new DOMParser().parseFromString(normalizedHtml, 'text/html');
        return this.serializeComparableRichTextNodes(Array.from(documentRef.body.childNodes)).trim();
    }

    private serializeComparableRichTextNodes(nodes: ChildNode[]): string {
        return nodes.map(node => this.serializeComparableRichTextNode(node)).join('');
    }

    private serializeComparableRichTextNode(node: ChildNode): string {
        if (node.nodeType === Node.TEXT_NODE) {
            return String(node.textContent ?? '').replace(/\u00a0/g, ' ');
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return '';
        }

        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        const content = this.serializeComparableRichTextNodes(Array.from(element.childNodes));

        if (tagName === 'p' && element.attributes.length === 0) {
            return `${content}\n`;
        }

        if (tagName === 'br') {
            return '\n';
        }

        const attributes = Array.from(element.attributes)
            .sort((left, right) => left.name.localeCompare(right.name))
            .map(attribute => ` ${attribute.name.toLowerCase()}="${attribute.value}"`)
            .join('');

        return `<${tagName}${attributes}>${content}</${tagName}>`;
    }

    private executeCreateIncident() {
        const payload = this.prepareCreateIncidentPayload();
        this.submitTemplateCreation('incident', payload);
    }

    private executeCreateMaintenance() {
        const payload = this.prepareMaintenancePayload();
        this.submitTemplateCreation('maintenance', payload);
    }

    private executeUpdateIncident() {
        const existingId = this.findTemplateId();
        if (!existingId) return;
        const payload = this.prepareUpdateIncidentPayload();
        this.submitTemplateUpdate('incident', existingId, payload);
    }

    private executeUpdateMaintenance() {
        const existingId = this.findTemplateId();
        if (!existingId) return;
        const payload = this.prepareMaintenancePayload();
        this.submitTemplateUpdate('maintenance', existingId, payload);
    }

    private findTemplateId(): number | null {
        const id = this.data?.template?.id ?? this.dataInput?.template?.id;
        if (!id) this.close();
        return id || null;
    }

    private submitTemplateCreation(type: TemplateType, payload: any) {
        const token = this.authService.getToken();
        this.isSubmitting = true;

        const request$ = type === 'incident'
            ? this.statusPageService.createIncidentTemplate(token, payload as CreateIncidentTemplatePayload)
            : this.statusPageService.createMaintenanceTemplate(token, payload as MaintenanceTemplatePayload);
        const templateLabel = type === 'incident' ? 'Incident Template' : 'Maintenance Template';

        request$.subscribe({
            next: (response) => {
                this.isSubmitting = false;
                this.layoutUtilsService.showActionNotification(`${templateLabel} created successfully`, MessageType.Create, environment.TOAST_MESSAGE_TIME, true, false);
                const createdTemplate = mapApiTemplateToViewModel(response, type);
                this.close({ template: createdTemplate });
            },
            error: (err) => {
                this.isSubmitting = false;
                this.handleError(err, 'Error creating template');
                this.cd.detectChanges();
            }
        });
    }

    private submitTemplateUpdate(type: TemplateType, id: number, payload: any) {
        const token = this.authService.getToken();
        this.isSubmitting = true;

        const updateRequest$ = type === 'incident'
            ? this.statusPageService.updateIncidentTemplate(token, id, payload as EditIncidentTemplatePayload)
            : this.statusPageService.updateMaintenanceTemplate(token, id, payload as MaintenanceTemplatePayload);
        const templateLabel = type === 'incident' ? 'Incident Template' : 'Maintenance Template';
        updateRequest$.subscribe({
            next: (response) => {
                this.isSubmitting = false;
                this.layoutUtilsService.showActionNotification(`${templateLabel} updated successfully`, MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
                const updatedTemplate = mapApiTemplateToViewModel(response, type);
                updatedTemplate.id = id;
                this.close({ template: updatedTemplate });
            },
            error: (err) => {
                this.isSubmitting = false;
                this.handleError(err, 'Error updating template');
                this.cd.detectChanges();
            }
        });
    }

    private handleError(err: any, defaultMsg: string) {
        const errorMsg = getStatusPageErrorMessage(err, defaultMsg);
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
    }

    private prepareCreateIncidentPayload(): CreateIncidentTemplatePayload {
        return {
            ...DEFAULT_CREATE_INCIDENT_TEMPLATE_PAYLOAD,
            name: this.form.value.name.trim(),
            titleTemplate: this.form.value.subject.trim(),
            descriptionTemplate: this.form.value.body,
            updateTemplates: [
                {
                    ...DEFAULT_CREATE_INCIDENT_TEMPLATE_PAYLOAD.updateTemplates[0],
                    message: this.form.value.body
                }
            ]
        };
    }

    private prepareUpdateIncidentPayload(): EditIncidentTemplatePayload {
        const original = this.data?.template?.original ?? this.dataInput?.template?.original;
        return {
            ...DEFAULT_EDIT_INCIDENT_TEMPLATE_PAYLOAD,
            name: this.form.value.name.trim(),
            titleTemplate: this.form.value.subject.trim(),
            descriptionTemplate: this.form.value.body,
            defaultStatus: original?.defaultStatus || 1,
            defaultImpact: original?.defaultImpact || 1,
            defaultServiceStatus: original?.defaultServiceStatus || 1,
            updateTemplates: [
                {
                    message: this.form.value.body,
                    status: original?.updateTemplates?.[0]?.status || 1,
                    notifySubscribers: original?.updateTemplates?.[0]?.notifySubscribers ?? true
                }
            ]
        };
    }

    private prepareMaintenancePayload(): MaintenanceTemplatePayload {
        const original = this.data?.template?.original ?? this.dataInput?.template?.original;
        const reminderHours = original?.reminderHoursBeforeStart ?? 0;
        return {
            ...DEFAULT_MAINTENANCE_TEMPLATE_PAYLOAD,
            name: this.form.value.name.trim(),
            titleTemplate: this.form.value.subject.trim(),
            descriptionTemplate: this.form.value.body,
            notifySubscribers: original?.notifySubscribers ?? true,
            reminderHoursBeforeStart: reminderHours > 0 ? reminderHours : 1,
            sendReminderAtStart: original?.sendReminderAtStart ?? true,
            sendReminderAtEnd: original?.sendReminderAtEnd ?? true,
            autoStartEnabled: original?.autoStartEnabled ?? true,
            autoCompleteEnabled: original?.autoCompleteEnabled ?? true
        };
    }

    cancel() {
        this.close();
    }


    private close(result?: any) {
        if (this.dialogRef) {
            this.dialogRef.close(result);
            return;
        }
        this.closed.emit(result);
    }
}
